<script setup lang="ts">
import { onMounted, ref } from "vue";

// import { useMainStore } from "@/store/main/index";
// import { StageData } from "../types/Stages.Interface";
import { isInView } from "@/composables/utils/isInView";

import PercentageCard from "./PercentageCard.vue";
import DateCard from "./DateCard.vue";

import { ImageInterface, ListDataInterface } from "@/types/ContentType";

const Picture = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Picture.vue")
);

/**
 * @author Giorgi Tseradze
 */

defineProps<{
  contentData: ListDataInterface;
  images: ImageInterface[];
  nextActive: boolean;
  prevActive: boolean;
}>();

const emit = defineEmits(["prev", "next"]);

const cardRef = ref();
const keyVal = ref(0);
const firstTrigger = ref(false);

const handlePrev = () => {
  emit("prev");
  keyVal.value++;
  setTimeout(() => {
    toggleAnimation();
  }, 450);
};

const handleNext = () => {
  emit("next");
  keyVal.value++;
  setTimeout(() => {
    toggleAnimation();
  }, 450);
};

const toggleAnimation = () => {
  cardRef?.value?.classList.remove("is-inview");
  cardRef?.value?.classList.add("is-inview");
};

const handleIsTrigger = () => {
  firstTrigger.value = isInView(cardRef.value!, 1);
};

onMounted(() => {
  setTimeout(() => {
    window?.addEventListener("scroll", handleIsTrigger);
  }, 2000);
});

onUnmounted(() => {
  window?.removeEventListener("scroll", handleIsTrigger);
});
</script>

<template>
  <Transition name="fade" mode="out-in">
    <section class="medium-box" :key="keyVal">
      <div ref="cardRef" class="medium-box__inner pos-rel" :class="firstTrigger ? 'is-inview' : ''">
        <DateCard
          :date="contentData?.date"
          :prevActive="prevActive"
          :nextActive="nextActive"
          @prev="handlePrev"
          @next="handleNext"
          class="medium-box__date"
        />
        <div
          class="medium-box__inner-image pos-rel"
          v-for="(photo, index) in images"
          :key="index"
          :style="`transition-delay: ${(1 + index) * 400}ms;`"
        >
          <Picture :devices="photo?.devices" class="img-absolute" alt="construction" />
        </div>
        <PercentageCard
          :style="`transition-delay: ${(images?.length + 1) * 400}ms;`"
          :contentData="contentData?.percent"
          class="medium-box__percent"
        />
      </div>
    </section>
  </Transition>
</template>

<style lang="scss">
.medium-box {
  &__date {
    margin-bottom: 24px;
    opacity: 0;
  }
  &__inner {
    display: grid;
    grid-template-columns: 33.3333333333% 33.3333333333% 33.3333333333%;
    // grid-template-columns: 30% 30% 30%;
    &.is-inview {
      .medium-box__inner-image {
        clip-path: inset(0 0 0 0);
        opacity: 1;
        @include easeOut(1000, clip-path);
        transition-delay: 200ms;
      }
      .medium-box__date {
        opacity: 1;
        @include easeOut(3000, opacity);
        transition-delay: 500ms;
      }
      .medium-box__percent {
        opacity: 1;
        @include easeOut(3000, opacity);
        transition-delay: 500ms;
      }
    }
    &-image {
      margin-right: 24px;
      margin-bottom: 24px;
      height: 0;
      padding-top: calc(100% / (306 / 368));
      clip-path: inset(100% 0 0 0);
      @include maxq(tablet) {
        padding-top: calc(100% / (220 / 264));
      }
    }
  }
  &__percent {
    opacity: 0;
  }
}
</style>
